<script setup lang="ts">
import { onMounted, type Component } from "vue";
import SelectDraggerMenu from "./SelectDraggerMenu.vue";
import EraserDraggerMenu from "./EraserDraggerMenu.vue";
import FreehandDraggerMenu from "./FreehandDraggerMenu.vue";
import TextDraggerMenu from "./TextDraggerMenu.vue";
import ShapeDraggerMenu from "./ShapeDraggerMenu.vue";
import PolylineDraggerMenu from "./PolylineDraggerMenu.vue";
import DimensionDraggerMenu from "./DimensionDraggerMenu.vue";
import ImageToolMenu from "./ImageToolMenu.vue";
import ViewTool from "./ViewTool.vue";
import RulerTool from "./RulerTool.vue";
import ObjectSnapTool from "./snap/ObjectSnapTool.vue";
import OrthoTool from "./snap/OrthoTool.vue";
import PolarTool from "./snap/PolarTool.vue";
import { toolbarColumns } from "@/stores/CanvasLayoutState";
import { withRestoreSuppressed } from "@/analytics/drawing-analytics";
import {
  releaseHiddenTools,
  visibleToolbarGroups,
} from "@/stores/ToolbarVisibilityState";
import { TOOLBAR_BUTTON_SIZE } from "@/utils/toolbar-layout";
import type { ToolbarToolId } from "@/utils/toolbar-tools";

/**
 * Outils de la barre, par groupe (cf. utils/toolbar-tools), en grille d'une
 * ou deux colonnes. Les outils masqués dans la modale Affichage n'y sont pas.
 */
const TOOLS: Record<
  ToolbarToolId,
  { component: Component; props?: Record<string, string> }
> = {
  select: { component: SelectDraggerMenu },
  eraser: { component: EraserDraggerMenu },
  freehand: { component: FreehandDraggerMenu },
  text: { component: TextDraggerMenu },
  line: { component: ShapeDraggerMenu, props: { shape: "line" } },
  rectangle: { component: ShapeDraggerMenu, props: { shape: "rectangle" } },
  ellipse: { component: ShapeDraggerMenu, props: { shape: "ellipse" } },
  polyline: { component: PolylineDraggerMenu },
  dimension: { component: DimensionDraggerMenu },
  image: { component: ImageToolMenu },
  origin: { component: RulerTool },
  zoomWindow: { component: ViewTool, props: { tool: "zoomWindow" } },
  zoomExtents: { component: ViewTool, props: { tool: "zoomExtents" } },
  objectSnap: { component: ObjectSnapTool },
  ortho: { component: OrthoTool },
  polar: { component: PolarTool },
};

// L'outil (ou l'aide) enregistré au dernier plan a pu être masqué depuis :
// effet du chargement, pas un geste de l'utilisateur (pas de suivi).
onMounted(() => withRestoreSuppressed(releaseHiddenTools));
</script>

<template>
  <div
    class="toolbar-grid"
    :style="{
      gridTemplateColumns: `repeat(${toolbarColumns}, ${TOOLBAR_BUTTON_SIZE}px)`,
    }"
  >
    <template v-for="(group, index) in visibleToolbarGroups" :key="group.id">
      <div v-if="index > 0" class="toolbar-divider" role="separator"></div>
      <component
        :is="TOOLS[tool].component"
        v-for="tool in group.tools"
        :key="tool"
        v-bind="TOOLS[tool].props"
      />
    </template>
  </div>
</template>

<style lang="scss" scoped>
// Dimensions reprises par utils/toolbar-layout (choix 1 ou 2 colonnes).
.toolbar-grid {
  display: grid;
  gap: 4px;
  justify-content: center;
}

// Toute la largeur de la grille : un groupe commence toujours sur une ligne.
.toolbar-divider {
  grid-column: 1 / -1;
  height: 1px;
  margin: 3px 4px;
  border-radius: 1px;
  background-color: rgba(255, 255, 255, 0.15);
}
</style>
